import React, { useState, useEffect, useRef, useCallback } from 'react';
import VoiceOrb from './VoiceOrb';
import MessageHistory from './MessageHistory';
import VoiceControls from './VoiceControls';
import LanguageModal from './LanguageModal';
import './VoiceChat.css';
import axios from 'axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../config/api';

const API_URL = API_ENDPOINTS.ASK;
const FREE_LIMIT = 5;
const PAID_LIMIT = 30;
const ENABLE_DIVINE_MUSIC = false; // Set to true to activate background ambient music flow
const ENABLE_OPTIMIZED_GREETING = true; // Use true for new fast logic, false for original logic
const NORMAL_MUSIC_VOLUME = 0; // 0.4% volume for idle state
const DUCKED_MUSIC_VOLUME = 0; // 0.2% volume when model is speaking

function VoiceChat() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [hasStarted, setHasStarted] = useState(false);
    const [activeMessageId, setActiveMessageId] = useState(null);
    
    // Chat limit state
    const [chatLimitInfo, setChatLimitInfo] = useState({
        isPaid: false,
        isYearly: false,
        messagesUsed: 0,
        remaining: FREE_LIMIT,
        limit: FREE_LIMIT,
        limitReached: false,
        loaded: false
    });
    const [showCounterAnim, setShowCounterAnim] = useState(false);
    
    // Sync selectedLanguage with search params
    const selectedLanguage = searchParams.get('lang');

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const persistentAudioRef = useRef(null);
    const isAudioUnlockedRef = useRef(false);
    const isCancelledRef = useRef(false);
    const mediaSourceRef = useRef(null);
    const streamReaderRef = useRef(null);

    // ── Divine Ambient Music refs ──────────────────────────────
    const divineAudioRef = useRef(null);          // The ambient <audio> element
    const divineStoppedForSessionRef = useRef(false); // Once true, never plays again this session
    const divineFadingRef = useRef(null);         // Holds setInterval id for fade ramps

    // Generate Session ID once per mount
    const [sessionId] = useState(() => 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));

    // ─── Fetch chat limit on mount & when user changes ─────────────────
    const fetchChatLimit = useCallback(async () => {
        if (!user?.id) return;
        try {
            const res = await axios.get(API_ENDPOINTS.CHAT_LIMIT, {
                params: { user_id: user.id }
            });
            if (res.data.success) {
                setChatLimitInfo({
                    isPaid: res.data.is_paid,
                    isYearly: res.data.is_yearly || false,
                    messagesUsed: res.data.messages_used,
                    remaining: res.data.remaining,
                    limit: res.data.limit || (res.data.is_paid ? PAID_LIMIT : FREE_LIMIT),
                    limitReached: res.data.limit_reached,
                    loaded: true
                });
            }
        } catch (err) {
            console.error("Failed to fetch chat limit:", err);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchChatLimit();
    }, [fetchChatLimit]);

    // Check if returning from successful payment 
    useEffect(() => {
        const paymentSuccess = searchParams.get('payment');
        if (paymentSuccess === 'success') {
            // Re-fetch limits (should now be paid)
            fetchChatLimit();
            // Clean URL
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('payment');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, fetchChatLimit, setSearchParams]);


    // ── Divine Music Helpers ───────────────────────────────────
    /**
     * Smoothly fade the divine ambient track to targetVol over durationMs.
     */
    const fadeDivineMusic = useCallback((targetVol, durationMs, permanent = false) => {
        const audio = divineAudioRef.current;
        if (!audio) return;

        if (divineFadingRef.current) {
            clearInterval(divineFadingRef.current);
            divineFadingRef.current = null;
        }

        const steps = 30;
        const intervalMs = durationMs / steps;

        // Ensure audio is playing if we are fading into a volume > 0
        if (targetVol > 0 && audio.paused) {
            audio.play().catch(e => console.warn('[Divine] Auto-play during fade failed:', e.message));
        }

        const startVol = audio.volume;
        const delta = (targetVol - startVol) / steps;
        let step = 0;

        divineFadingRef.current = setInterval(() => {
            step++;
            const next = Math.min(1, Math.max(0, startVol + delta * step));
            audio.volume = next;
            if (step >= steps) {
                clearInterval(divineFadingRef.current);
                divineFadingRef.current = null;
                audio.volume = targetVol;
                if (targetVol === 0) {
                    audio.pause();
                    if (permanent) {
                        divineStoppedForSessionRef.current = true;
                        console.log('[Divine] Background music muted PERMANENTLY for session.');
                    }
                }
            }
        }, intervalMs);
    }, []);

    const startDivineMusic = useCallback(() => {
        if (!ENABLE_DIVINE_MUSIC) return; // Feature toggle

        const audio = divineAudioRef.current;
        if (!audio || divineStoppedForSessionRef.current) return;

        console.log('[Divine] Fading in ambient music (Post-Greeting)...');
        audio.volume = 0;
        audio.loop = true;
        
        audio.play().then(() => {
            fadeDivineMusic(NORMAL_MUSIC_VOLUME, 3000); // Step 3: Fade to target over 3s
        }).catch(e => {
            console.warn('[Divine] Ambient play skipped (file missing or interaction required):', e.message);
        });
    }, [fadeDivineMusic]);

    const stopAudio = useCallback((reason = "unspecified", isTransitioningToListening = false) => {
        console.log(`Stopping audio (Reason: ${reason})`);
        isCancelledRef.current = true;

        if (streamReaderRef.current) {
            try { streamReaderRef.current.cancel('User stopped'); } catch (e) {}
            streamReaderRef.current = null;
        }
        if (mediaSourceRef.current) {
            try {
                if (mediaSourceRef.current.readyState === 'open') {
                    mediaSourceRef.current.endOfStream();
                }
            } catch (e) {}
            mediaSourceRef.current = null;
        }

        const audio = persistentAudioRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }

        setIsSpeaking(false);
        setActiveMessageId(null);

        // -- Resume background music if it was ducked/paused --
        // EXCEPT if we are about to start the mic (listening)
        if (divineAudioRef.current && !divineStoppedForSessionRef.current && !isTransitioningToListening) {
            fadeDivineMusic(NORMAL_MUSIC_VOLUME, 1000);
        }
    }, [fadeDivineMusic]);
    // ─────────────────────────────────────────────────────────

    const unlockAudio = useCallback(async () => {
        if (isAudioUnlockedRef.current) return;

        console.log("Unlocking audio systems for iOS/Mobile...");
        try {
            if (!audioContextRef.current) {
                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                if (AudioContextClass) {
                    audioContextRef.current = new AudioContextClass();
                }
            }

            if (audioContextRef.current) {
                if (audioContextRef.current.state === 'suspended') {
                    await audioContextRef.current.resume();
                }

                const buffer = audioContextRef.current.createBuffer(1, 1, 22050);
                const node = audioContextRef.current.createBufferSource();
                node.buffer = buffer;
                node.connect(audioContextRef.current.destination);
                node.start(0);
                node.onended = () => {
                    node.disconnect();
                    console.log("Web Audio systems primed");
                };
            }

            if (persistentAudioRef.current) {
                persistentAudioRef.current.setAttribute('playsinline', 'true');
                persistentAudioRef.current.setAttribute('webkit-playsinline', 'true');
                persistentAudioRef.current.preload = 'auto';

                persistentAudioRef.current.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
                const playPromise = persistentAudioRef.current.play();
                if (playPromise !== undefined) {
                    await playPromise.catch(e => console.warn("Silent play failed, but intent was registered:", e));
                    persistentAudioRef.current.pause();
                }
            }

            isAudioUnlockedRef.current = true;
            console.log("Audio systems successfully unlocked");
        } catch (err) {
            console.error("Audio systems unlock failed:", err);
        }
    }, []);

    // Global listener to unlock audio on first interaction
    useEffect(() => {
        const handleInteraction = () => {
            console.log("User interaction detected, attempting to unlock audio...");
            unlockAudio();
        };

        window.addEventListener('touchstart', handleInteraction, { once: true, capture: true });
        window.addEventListener('click', handleInteraction, { once: true, capture: true });
        window.addEventListener('mousedown', handleInteraction, { once: true, capture: true });

        return () => {
            window.removeEventListener('touchstart', handleInteraction, { capture: true });
            window.removeEventListener('click', handleInteraction, { capture: true });
            window.removeEventListener('mousedown', handleInteraction, { capture: true });
        };
    }, [unlockAudio]);

    const speakText = useCallback(async (text, messageId = null, audioUrl = null, languageOverride = null, onEnd = null) => {
        // Skip toggle check if we're providing a direct audioUrl (e.g. for optimized greeting parts)
        if (isSpeaking && activeMessageId === messageId && messageId !== null && !audioUrl) {
            stopAudio("toggle-stop");
            return;
        }

        stopAudio("new-speech-request");
        isCancelledRef.current = false;
        setIsSpeaking(true);
        setActiveMessageId(messageId);

        // -- Audio Ducking: Lower background music during TTS --
        const musicIsActive = divineAudioRef.current && !divineStoppedForSessionRef.current;
        if (musicIsActive) {
            fadeDivineMusic(DUCKED_MUSIC_VOLUME, 500); // Duck background music
        }

        const currentLang = languageOverride || selectedLanguage;
        const baseUrl = API_ENDPOINTS.ASK.split('/api/ask')[0];
        
        let fullUrl = null;
        if (audioUrl) {
            if (audioUrl.startsWith('http') || audioUrl.startsWith('blob:')) {
                fullUrl = audioUrl;
            } else {
                fullUrl = `${baseUrl}${audioUrl}`;
            }
        }

        try {
            const audio = persistentAudioRef.current;
            if (!audio) throw new Error('Audio element missing');
            audio.setAttribute('playsinline', 'true');
            audio.setAttribute('webkit-playsinline', 'true');

            let src;
            if (fullUrl) {
                src = fullUrl;
            } else {
                console.log('Sending text to /api/speak:', { text: text?.substring(0, 50), language: currentLang });
                const response = await axios.post(
                    API_ENDPOINTS.SPEAK,
                    { text, language: currentLang },
                    { responseType: 'blob', timeout: 60000 }
                );
                src = URL.createObjectURL(response.data);
            }

            if (isCancelledRef.current) {
                console.warn("Speech cancelled before playback started:", text.substring(0, 30));
                if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
                setIsSpeaking(false);
                setActiveMessageId(null);
                
                // Resume music if it was active
                if (musicIsActive) {
                    fadeDivineMusic(NORMAL_MUSIC_VOLUME, 1000);
                }
                return;
            }

            audio.src = src;
            audio.load();
            audio.onended = () => {
                setIsSpeaking(false);
                setActiveMessageId(null);
                if (src.startsWith('blob:')) URL.revokeObjectURL(src);
                
                // -- Resume music after TTS ends --
                if (musicIsActive) {
                    fadeDivineMusic(NORMAL_MUSIC_VOLUME, 1000);
                }

                if (onEnd) onEnd();
            };
            await audio.play();
            console.log('✅ Audio playing —', currentLang === 'hi' ? 'Azure Aarav (Hindi)' : (currentLang === 'ta' ? 'Azure Valluvar (Tamil)' : 'Edge TTS (English)'));

        } catch (err) {
            console.error('❌ Playback failed:', err);
            setIsSpeaking(false);
            setActiveMessageId(null);

            // Resume music on error
            if (musicIsActive) {
                fadeDivineMusic(NORMAL_MUSIC_VOLUME, 1000);
            }

            if (onEnd) onEnd();
        }
    }, [stopAudio, isSpeaking, activeMessageId, selectedLanguage, fadeDivineMusic]);


    const handleAudioUpload = async (audioBlob) => {
        setIsLoading(true);
        setTranscript('');

        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume().catch(() => { });
        }

        const formData = new FormData();
        formData.append('audio', audioBlob, 'record.webm');

        try {
            const res = await axios.post(API_ENDPOINTS.TRANSCRIBE, formData);
            if (res.data.success && res.data.text) {
                const transcriptText = res.data.text;
                await handleVoiceInput(transcriptText);
            } else if (res.data.is_silent) {
                // RMS energy / word count guard detected silence — reset cleanly
                console.log("🤫 [Frontend] Server detected silence. Resetting to idle.");
                setIsLoading(false);
                setTranscript('');
            } else {
                setTranscript("Could not transcribe.");
                setIsLoading(false);
            }
        } catch (e) {
            console.error("Transcription error", e);
            setTranscript("Error understanding audio.");
            setIsLoading(false);
        }
    };

    const handleVoiceInput = useCallback(async (text) => {
        if (!text.trim()) return;

        // ─── Block input if limit reached ──────────────────────
        if (!chatLimitInfo.isPaid && chatLimitInfo.limitReached) {
            return; // Should not reach here since controls are disabled
        }

        setTranscript(text);
        setIsLoading(true);

        try {
            const startTime = performance.now();

            const response = await axios.post(API_URL, {
                question:      text,
                include_audio: true,
                session_id:    sessionId,
                user_id:       user?.id,
                language:      selectedLanguage
            }, { timeout: 60000 });

            // ─── Check if backend returned limit_reached ──────────
            if (response.data.limit_reached) {
                setChatLimitInfo(prev => ({
                    ...prev,
                    limitReached: true,
                    remaining: 0,
                    limit: response.data.limit || prev.limit,
                    messagesUsed: response.data.messages_used || prev.messagesUsed
                }));
                setIsLoading(false);
                setTranscript('');
                return;
            }

            const textTime = performance.now() - startTime;
            console.log(`⏱️ Text response in ${textTime.toFixed(0)}ms`);

            const krishnaMessageId = Date.now();
            const newInteraction = {
                timestamp: new Date().toISOString(),
                question: text,
                answer: response.data.answer || 'क्षमा करें, मैं अभी उत्तर देने में असमर्थ हूँ।',
                shlokas: response.data.shlokas || []
            };
            setMessages(prev => [...prev, newInteraction]);

            // ─── Update chat limit counter ────────────────────────
            // Increment ONLY for real questions: not rejected, not small talk
            const hasLimit = chatLimitInfo.limit > 0 && chatLimitInfo.limit < 1000;
            if (hasLimit && !response.data.rejected && !response.data.is_small_talk) {
                setChatLimitInfo(prev => {
                    const newUsed = prev.messagesUsed + 1;
                    const currentLimit = prev.limit;
                    const newRemaining = Math.max(0, currentLimit - newUsed);
                    return {
                        ...prev,
                        messagesUsed: newUsed,
                        remaining: newRemaining,
                        limitReached: newUsed >= currentLimit
                    };
                });
                // Trigger counter animation
                setShowCounterAnim(true);
                setTimeout(() => setShowCounterAnim(false), 600);
            } else if (response.data.is_small_talk) {
                console.log("💬 Small talk — chat limit NOT decremented (free talk).");
            } else if (response.data.rejected) {
                console.log("Chat limit not decremented for irrelevant query.");
            }

            // Mark user as 'Returning' now that they asked a question
            const userKey = user?.id ? `welcome_seen_${user.id}` : 'welcome_seen_guest';
            localStorage.setItem(userKey, 'true');

            speakText(newInteraction.answer, krishnaMessageId);

        } catch (error) {
            console.error('Error:', error);
            
            // Check if the 403 limit_reached response came back
            if (error.response && error.response.status === 403 && error.response.data?.limit_reached) {
                setChatLimitInfo(prev => ({
                    ...prev,
                    limitReached: true,
                    remaining: 0,
                    messagesUsed: error.response.data.messages_used || prev.messagesUsed
                }));
                setIsLoading(false);
                setTranscript('');
                return;
            }

            const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
            const errorMsgId = Date.now() + 1;
            const errorMsg = {
                id:        errorMsgId,
                type:      'krishna',
                text:      isTimeout
                    ? 'सर्वर जाग रहा है, कृपया 30 सेकंड बाद पुनः प्रयास करें।'
                    : 'क्षमा करें, कुछ गलत हो गया। कृपया पुनः प्रयास करें।',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMsg]);
            speakText(errorMsg.text, errorMsgId);
        } finally {
            setIsLoading(false);
            setTranscript('');
        }
    }, [speakText, user, sessionId, selectedLanguage, chatLimitInfo]);

    const isStartingRef = useRef(false);

    // Start Journey Handler (called after language is chosen)
    const handleStartJourney = useCallback(async (lang) => {
        if (isStartingRef.current) {
            console.warn("handleStartJourney already in progress, skipping duplicate call.");
            return;
        }
        isStartingRef.current = true;
        console.log("Starting journey - unlocking audio and sending welcome check...");
        
        try {
            await unlockAudio();
            setHasStarted(true);

            // Fetch name from LocalStorage (Step 1 Cache) or fall back to context user
            const cachedName = localStorage.getItem('user_first_name');
            const firstName = cachedName || (user?.name ? user.name.split(' ')[0] : 'Seeker');
            
            // ─── Welcome Logic (History Check) ─────────────────────────────
            // Strictly determine returning status based on actual message count from DB or history
            const hasHistory = (chatLimitInfo.messagesUsed > 0) || (messages.length > 0);
            
            const isEnglish = lang === 'en';
            const isTamil = lang === 'ta';
            const welcomeMsgId = Date.now();

            let part1Text = "";
            let part2Text = "";
            let fullText = "";

            if (hasHistory) {
                part1Text = isEnglish ? `Namaste ${firstName}!` : (isTamil ? `Namaste ${firstName}!` : `नमस्ते ${firstName}!`);
                part2Text = isEnglish 
                    ? `I hope you learned from our previous conversation. What is your concern for today?`
                    : (isTamil 
                        ? `எங்கள் முந்தைய உரையாடலில் இருந்து நீங்கள் கற்றுக்கொண்டீர்கள் என்று நம்புகிறேன். இன்று உங்கள் கவலை என்ன?`
                        : `आशा है कि आपने हमारी पिछली बातचीत से कुछ सीखा होगा। मुझे बताएं कि आज आपकी क्या चिंता है?`);
                fullText = `${part1Text} ${part2Text}`;
            } else {
                part1Text = isEnglish ? `Namaste ${firstName}!` : (isTamil ? `Namaste ${firstName}!` : `नमस्ते ${firstName}!`);
                part2Text = isEnglish 
                    ? `I am Krishna. You can ask me anything about your life and concerns. How can I help you today?`
                    : (isTamil 
                        ? `நான் கிருஷ்ணன். உங்கள் வாழ்க்கை மற்றும் கவலைகள் பற்றி நீங்கள் எதையும் என்னிடம் கேட்கலாம். இன்று நான் உங்களுக்கு எப்படி உதவ முடியும்?`
                        : `मैं श्री कृष्ण हूँ। आप मुझसे अपने जीवन और चिंताओं के बारे में कुछ भी पूछ सकते हैं। आज मैं आपकी क्या मदद कर सकता हूँ?`);
                fullText = `${part1Text} ${part2Text}`;
                
                // We no longer set localStorage here. 
                // The user only becomes a 'returning user' once they ask their first real question.
                if (user?.id) {
                    axios.post(API_ENDPOINTS.MARK_WELCOME_SEEN, { user_id: user.id })
                        .catch(e => console.warn("Failed to mark welcome as seen", e));
                }
            }

            const welcomeMsg = {
                id: welcomeMsgId, // Add explicit ID
                timestamp: new Date().toISOString(),
                question: "",
                answer: fullText,
                isWelcome: true
            };

            setMessages(prev => {
                if (prev.length > 0) return prev;
                return [welcomeMsg];
            });

            // Handle Greeting using Feature Toggle with Pre-fetching for Part 2
            if (ENABLE_OPTIMIZED_GREETING) {
                console.log("⚡ [Optimized] Running multi-part greeting with pre-fetching...");
                
                // 1. Play Part 1 immediately (Short text = High Priority)
                speakText(part1Text, welcomeMsgId, null, lang, async () => {
                    console.log("⚡ [Optimized] Part 1 done, playing pre-fetched Part 2...");
                    
                    try {
                        const response = await part2Promise;
                        const src = URL.createObjectURL(response.data);
                        // Start Part 2 IMMEDIATELY (No timeout to preserve user gesture context on mobile)
                        speakText(part2Text, `${welcomeMsgId}_p2`, src, lang, () => {
                            startDivineMusic();
                        });
                    } catch (err) {
                        console.warn("⚡ [Optimized] Part 2 pre-fetch failed, falling back to dynamic fetch.", err);
                        speakText(part2Text, `${welcomeMsgId}_p2`, null, lang, () => {
                             startDivineMusic();
                        });
                    }
                });

                // 2. Start pre-fetching Part 2 in background AFTER Part 1 request is fired
                const part2Promise = axios.post(
                    API_ENDPOINTS.SPEAK,
                    { text: part2Text, language: lang },
                    { responseType: 'blob', timeout: 60000 }
                );
            } else {
                // Fallback to original blocking logic
                console.log("🛡️ [Fallback] Running original greeting logic...");
                await speakText(fullText, welcomeMsgId, null, lang, () => {
                    startDivineMusic();
                });
            }
        } finally {
            isStartingRef.current = false;
        }
    }, [unlockAudio, speakText, user, messages.length, startDivineMusic, chatLimitInfo.messagesUsed]);

    // Called when user picks a language in the modal
    const handleLanguageSelect = useCallback(async (lang) => {
        console.log("Language selected, updating URL and starting journey:", lang);
        await unlockAudio();
        setSearchParams({ lang });
        await handleStartJourney(lang);
    }, [unlockAudio, setSearchParams, handleStartJourney]);

    // Load previous history on mount
    useEffect(() => {
        if (user?.id) {
            const fetchHistory = async () => {
                try {
                    const response = await axios.get(API_ENDPOINTS.HISTORY, {
                        params: { user_id: user.id }
                    });
                    if (response.data.success && response.data.history) {
                        setMessages(response.data.history);
                    }
                } catch (err) {
                    console.error("Failed to fetch history", err);
                }
            };
            fetchHistory();
        }
    }, [user?.id]);

    // Reset hasStarted when language is cleared
    useEffect(() => {
        if (!selectedLanguage) {
            setHasStarted(false);
            setMessages([]);
            stopAudio("lang-parameter-removed");
        }
    }, [selectedLanguage, stopAudio]);

    // Stop audio when location/route changes
    useEffect(() => {
        return () => {
            console.log("Route changing/unmounting - stopping audio");
            isCancelledRef.current = true;
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            if (divineAudioRef.current) {
                divineAudioRef.current.pause();
                divineAudioRef.current.src = "";
            }
        };
    }, [location.pathname]);

    // Initialise Divine Ambient Background Music
    useEffect(() => {
        if (!ENABLE_DIVINE_MUSIC) return; 

        const bgAudio = new Audio('/sacred-flute.mp3');
        bgAudio.loop = true;
        bgAudio.volume = 0;
        bgAudio.preload = 'auto';
        bgAudio.setAttribute('playsinline', 'true');
        bgAudio.setAttribute('webkit-playsinline', 'true');
        divineAudioRef.current = bgAudio;
        console.log('[Divine] Ambient background element ready.');

        return () => {
            if (divineFadingRef.current) clearInterval(divineFadingRef.current);
            bgAudio.pause();
            bgAudio.src = '';
            divineStoppedForSessionRef.current = false;
        };
    }, []);

    const toggleListening = () => {
        unlockAudio();

        // ─── Block if limit reached ──────────────────────────
        if (!chatLimitInfo.isPaid && chatLimitInfo.limitReached) {
            return;
        }

        if (!isListening && (isLoading || isSpeaking)) {
            console.log("Mic requested while still loading or speaking - stopping audio first");
            if (isSpeaking) stopAudio("mic-gesture-stop", true);
            // Proceed to start recording immediately
        }

        if (isListening) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            setIsListening(false);
        } else {
            // Mic clicked -> Fade out music during listening (but not permanent)
            if (!divineStoppedForSessionRef.current) {
                fadeDivineMusic(0, 500, false); 
            }

            if (isSpeaking) {
                stopAudio("mic-gesture-stop");
            }

            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert('Audio recording is not supported in this browser.');
                return;
            }

            navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
                let mimeType = 'audio/webm';
                if (!MediaRecorder.isTypeSupported(mimeType)) {
                    mimeType = 'audio/mp4';
                    if (!MediaRecorder.isTypeSupported(mimeType)) {
                        mimeType = '';
                    }
                }

                const mediaRecorder = new MediaRecorder(stream, { mimeType });
                mediaRecorderRef.current = mediaRecorder;
                audioChunksRef.current = [];

                mediaRecorder.ondataavailable = event => {
                    if (event.data.size > 0) audioChunksRef.current.push(event.data);
                };

                mediaRecorder.onstop = async () => {
                    stream.getTracks().forEach(track => track.stop());
                    if (audioChunksRef.current.length > 0) {
                        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
                        
                        // Resume music after recording stops (it will duck again if Krishna speaks)
                        if (!divineStoppedForSessionRef.current) {
                            fadeDivineMusic(NORMAL_MUSIC_VOLUME, 800);
                        }
                        
                        await handleAudioUpload(audioBlob);
                    }
                };

                mediaRecorder.start();
                setIsListening(true);
                setHasStarted(true);
            }).catch(e => {
                console.error("Microphone access denied:", e);
                alert("Please allow microphone access to use voice chat.");
            });
        }
    };

    const stopSpeaking = () => {
        stopAudio("manual-stop-button");
    };

    const clearHistory = async (date = null) => {
        console.log(date ? `Clearing history for date: ${date}` : "Clearing all history");
        
        if (isSpeaking) {
            stopAudio("clearing-history");
        }

        if (user?.id) {
            try {
                await axios.delete(API_ENDPOINTS.HISTORY, {
                    data: { user_id: user.id, date: date }
                });
            } catch (err) {
                console.error("Failed to clear DB history", err);
            }
        }

        if (date) {
            setMessages(prev => prev.filter(msg => {
                const msgDate = msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp);
                const dateKey = msgDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                return dateKey !== date;
            }));
        } else {
            setMessages([]);
            setHasStarted(false);
            setSearchParams({});
        }
    };

    // ─── Logic for showing the "Gate" ──────────────────────────────────
    // We block input immediately when the limit is reached
    // Support for limited paid plans (e.g. coupons)
    const isInputBlocked = chatLimitInfo.loaded && chatLimitInfo.limitReached;
    
    // We only show the visual Overlay AFTER the current response is finished 
    // (not speaking and not loading) so the user can see/hear the 5th (or 30th) answer.
    const showLimitOverlay = isInputBlocked && !isSpeaking && !isLoading;
    
    // Determine if we should show the counter badge
    // Hidden for: unlimited users (limit >= 900,000) or when limit is reached
    const showCounter = chatLimitInfo.loaded && !chatLimitInfo.limitReached &&
        (chatLimitInfo.limit > 0 && chatLimitInfo.limit < 900000);

    return (
        <div className="voice-chat-container">
            {/* Language Selection Modal — shown until user picks a language */}
            {!selectedLanguage && (
                <LanguageModal 
                    onSelect={handleLanguageSelect} 
                    onBack={() => navigate('/')}
                />
            )}

            {/* ─── Limit Reached Overlay ───────────────────────────────── */}
            {showLimitOverlay && (
                <div className="limit-overlay" id="limit-reached-overlay">
                    <div className="limit-overlay-content">
                        <div className="limit-overlay-icon">{chatLimitInfo.isPaid ? '⏳' : '🔒'}</div>
                        <h2 className="limit-overlay-title">
                            {chatLimitInfo.isPaid ? 'Monthly Limit Reached' : 'Free Limit Reached'}
                        </h2>
                        <p className="limit-overlay-text">
                            {chatLimitInfo.isPaid 
                                ? (chatLimitInfo.isYearly
                                    ? `You've used all ${chatLimitInfo.limit} divine conversations included in your Yearly Plan. Please wait for the next billing cycle or contact support.`
                                    : (chatLimitInfo.limit <= 30 
                                        ? `You've used all ${chatLimitInfo.limit} divine conversations included in your plan. Upgrade to the Premium Plan for unlimited spiritual guidance.`
                                        : `You've used all ${chatLimitInfo.limit} divine conversations for this month. Please wait for the next billing cycle or contact support.`))
                                : `You've used all ${FREE_LIMIT} free divine conversations. Upgrade to continue your spiritual journey with unlimited guidance.`
                            }
                        </p>
                        
                        {/* --- Smart Nudge for Coupon --- */}
                        {(!chatLimitInfo.isPaid || chatLimitInfo.limit <= 30) && (
                            <div className="coupon-nudge">
                                <span className="nudge-sparkle">✨</span>
                                <span>Apply <strong>KRISHNA499</strong> to get MonthlyPlan in just 499 rs</span>
                            </div>
                        )}
                        {(!chatLimitInfo.isPaid || chatLimitInfo.limit <= 30) ? (
                            <button 
                                className="limit-overlay-cta"
                                id="upgrade-now-button"
                                onClick={() => navigate('/checkout?plan=basic')}
                            >
                                <span className="cta-sparkle">✨</span>
                                Upgrade to Monthly Plan
                                <span className="cta-arrow">→</span>
                            </button>
                        ) : (
                            <button 
                                className="limit-overlay-cta"
                                onClick={() => navigate('/')}
                                style={{ background: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)' }}
                            >
                                Back to Home
                            </button>
                        )}
                        {!chatLimitInfo.isPaid && (
                            <button 
                                className="limit-overlay-back"
                                onClick={() => navigate('/')}
                            >
                                Back to Home
                            </button>
                        )}
                    </div>
                </div>
            )}


            {/* Header */}
            <header className="app-header">
                <button className="icon-button back-button" onClick={() => {
                    stopAudio("back-button-nav");
                    if (selectedLanguage) {
                        navigate(-1);
                    } else {
                        navigate('/');
                    }
                }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                <div className="header-title-container">
                    <span className="logo-icon">🕉️</span>
                    <span className="header-title">Divine Voice</span>
                </div>

                <button
                    className="history-toggle-new"
                    onClick={() => {
                        if (isSpeaking) {
                            stopAudio("history-toggle");
                        }
                        setShowHistory(!showHistory);
                    }}
                    title="View past dialogues"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>History</span>
                </button>
            </header>

            {/* Main Voice Interface */}
            <main className="main-content">
                {!hasStarted && selectedLanguage && (
                    <div className="hero-section">
                        <h1 className="hero-title">
                            Seek Guidance <br />
                            <span className="highlight">From The Divine</span>
                        </h1>
                        <div className="quick-actions">
                            <button className="action-chip active" onClick={() => handleStartJourney(selectedLanguage)}>
                                Start Journey
                            </button>
                            <button className="action-chip" onClick={() => setShowHistory(true)}>
                                History
                            </button>
                        </div>
                    </div>
                )}

                <div className="orb-section">
                    <h2 className="section-label">Soul Connection</h2>
                    <VoiceOrb
                        isListening={isListening}
                        isSpeaking={isSpeaking}
                        isLoading={isLoading}
                    />
                </div>

                {/* Status Text & Instructions */}
                <div className="status-container">
                    <div className="status-text" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        {showLimitOverlay ? (
                            <span style={{ color: '#f87171', fontWeight: '600' }}>Chat limit reached — Upgrade to continue</span>
                        ) : isListening ? (
                            <>
                                <span style={{ color: '#fff', fontSize: '1.2rem', textShadow: '0 0 10px rgba(255,255,255,0.5)' }}>Listening to your soul...</span>
                                <span style={{ color: '#ffb347', fontSize: '0.9rem', animation: 'pulse 2s infinite', fontWeight: 'bold' }}>
                                    (Tap the mic again to finish speaking)
                                </span>
                            </>
                        ) : isSpeaking ? (
                            <span style={{ color: '#fff' }}>Krishna is guiding...</span>
                        ) : isLoading ? (
                            <span style={{ color: '#fff' }}>Consulting the Gita...</span>
                        ) : (
                            <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{transcript ? `"${transcript}"` : 'Tap to Connect'}</span>
                        )}
                    </div>
                </div>


                {/* Voice Controls */}
                <VoiceControls
                    isListening={isListening}
                    isSpeaking={isSpeaking}
                    isLoading={isLoading}
                    onToggleListening={toggleListening}
                    onStopSpeaking={stopSpeaking}
                    disabled={isInputBlocked}
                />
                
                {/* Remaining Counter below Mic */}
                {showCounter && (
                    <div className="mic-counter-container">
                        <div className={`mic-counter-mini ${showCounterAnim ? 'pulse-anim' : ''}`}>
                            <span className="mic-counter-number">{chatLimitInfo.remaining}</span>
                            <span className="mic-counter-text">
                                {chatLimitInfo.remaining === 1 ? 'conversation left' : 'conversations left'}
                            </span>
                        </div>
                    </div>
                )}
            </main>

            {/* Message History Sidebar */}
            <MessageHistory
                messages={messages}
                isOpen={showHistory}
                onSpeak={speakText}
                activeMessageId={activeMessageId}
                onClose={() => {
                    if (isSpeaking) {
                        stopAudio("history-close");
                    }
                    setShowHistory(false);
                }}
                onClearHistory={clearHistory}
            />

            {/* Hidden fallback audio element */}
            <audio
                ref={persistentAudioRef}
                style={{ display: 'none' }}
                preload="auto"
                playsInline
            />

        </div>
    );
}

export default VoiceChat;
/*
auto const = 2
auto.console'("streaming auto : python")

for i in range(divinemusic.auto.consoe(!=1)):
    const = i++;
    idea = const+1;
    console.log(idea);
    function = idea+const;
    console.log(function);

    divinemusic.clicks(user.id) != chat_session_id :
    divinemusic.clicks(user.id) = chat_session_id;
    console.log(divinemusic.clicks(user.id));
    i++ ; 
    while(i==2):
        console.log("i is 2");
        break;
    
    express.json.embedding.file(!=console)
    console.log(express.json.embedding.file);
    
    json-?file : file ++ requires
    <audio 
        src={audioUrl}
        autoPlay
        controls
        style={{ display: 'none' }}
    />

    autoplay()
    if(divinemusic.clicks(user.id) == chat_session_id):
        console.log("chat session id is same");
        break;
    else:
        divinemusic.clicks(user.id) = chat_session_id;
        console.log(divinemusic.clicks(user.id));
        break;

    user_session_states ==
*/